import React from 'react';
import { TrendingUp, ArrowLeft } from 'lucide-react';
import DoodleBackground from '../components/DoodleBackground';

const BusinessModelCanvas = () => {
  return (
    <div>
      {/* Simple Nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(255,255,255,.97)', boxShadow: '0 2px 20px rgba(120,81,169,.08)',
        backdropFilter: 'blur(16px)', padding: '10px 0',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
            <img
              src="/logo.png"
              alt="Spee'V'Dee"
              style={{ width: 44, height: 44, borderRadius: '50%', filter: 'drop-shadow(0 2px 4px rgba(120,81,169,.3))' }}
            />
            <span className="logo-text">
              <span className="bk">Spee</span>
              <span className="vv">'V'</span>
              <span className="bk">Dee</span>
            </span>
          </a>
          <a href="/" data-testid="back-home-link" className="btn btn-outline" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            color: '#7851A9', fontWeight: 700, fontSize: 14, textDecoration: 'none',
            padding: '8px 20px', borderRadius: '50px', border: '2px solid #7851A9',
            transition: 'all 0.3s',
          }}>
            <ArrowLeft size={16} /> Back to Home
          </a>
        </div>
      </nav>

      {/* BMC Content */}
      <section style={{ background: '#f9fafb', position: 'relative', overflow: 'hidden', paddingTop: 100, paddingBottom: 80 }}>
        <DoodleBackground />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <div className="section-header">
            <span className="section-tag" style={{ background: 'rgba(120,81,169,.1)', color: '#7851A9' }}>FOR INVESTORS</span>
            <h2 data-testid="bmc-title">Business Model Canvas</h2>
            <p style={{ fontStyle: 'italic', color: '#7851A9', fontWeight: 600 }}>Airtight & Watertight | App Fully Developed | Ready for Global Launch</p>
          </div>

          {/* Row 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20, position: 'relative', zIndex: 1 }}>
            <div className="bmc-card">
              <h4>Key Partners</h4>
              <p><strong>Everyday Couriers:</strong> Drivers, cyclists, walkers, students, commuters, freelancers, retirees — anyone going somewhere can deliver</p>
              <p><strong>Strategic:</strong> Airlines (Delta, Emirates, United), flight attendant unions, airport authorities, customs & border agencies, hotel chains, crew lounges</p>
              <p><strong>Service:</strong> Stripe, PayPal, Payoneer, AMEX/Allianz insurance, AWS/Google Cloud, ID.me/Onfido verification</p>
              <p><strong>Distribution:</strong> Universities, gig economy platforms, travel agencies, digital nomad communities, expat forums, local courier networks</p>
            </div>
            <div className="bmc-card">
              <h4>Key Activities</h4>
              <p><strong>Core:</strong> Platform 24/7 uptime, user verification, subscription billing, dispute resolution, real-time tracking</p>
              <p><strong>Growth:</strong> Airline partnership development, flight attendant recruitment, targeted marketing, referral programs</p>
              <p><strong>Safety:</strong> Background checks, package inspection, insurance claims, rating system integrity</p>
            </div>
            <div className="bmc-card" style={{ background: 'linear-gradient(135deg, #7851A9, #5B2D8E)', color: '#fff' }}>
              <h4 style={{ color: '#fff' }}>Value Propositions</h4>
              <p><strong>Senders:</strong> $1/mo unlimited posting, ultra-low cost global delivery, verified flight attendant couriers, real-time tracking, secure escrow</p>
              <p><strong>Flight Attendants:</strong> $1/mo to earn $500-$2,000/month extra on existing routes, no schedule changes, crew luggage utilization</p>
              <p><strong>Travelers:</strong> $1/mo to offset travel costs, flexible itinerary-based work, spare luggage monetization</p>
            </div>
            <div className="bmc-card">
              <h4>Customer Relationships</h4>
              <p><strong>Automated:</strong> In-app 24/7 AI support, push notifications, automated subscription renewal</p>
              <p><strong>Community:</strong> Crew-only forums, two-way ratings, verified badges, referral rewards</p>
              <p><strong>High-Touch:</strong> Dedicated crew support line, airline account managers, onboarding webinars, airport ambassadors</p>
            </div>
            <div className="bmc-card">
              <h4>Customer Segments</h4>
              <p><strong>Primary Couriers:</strong> 500,000+ flight attendants globally with regular international routes, already background-checked</p>
              <p><strong>Secondary Couriers:</strong> Tourists, business travelers, digital nomads, students, expatriates</p>
              <p><strong>Senders:</strong> Individuals (gifts, docs), small e-commerce, expats, corporate clients, families</p>
            </div>
          </div>

          {/* Row 2 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 20, position: 'relative', zIndex: 1 }}>
            <div className="bmc-card">
              <h4>Key Resources</h4>
              <p><strong>Physical:</strong> Fully developed iOS & Android app, scalable cloud infrastructure, verified user database with airline crew flags</p>
              <p><strong>Intellectual:</strong> Brand trust, airline partnership agreements, legal framework for 100+ countries, customs templates</p>
              <p><strong>Human:</strong> Flight attendant ambassador network, multilingual support, airline relationship managers</p>
              <p><strong>Financial:</strong> Escrow infrastructure, payment gateway integrations, insurance partnerships</p>
            </div>
            <div className="bmc-card">
              <h4>Channels</h4>
              <p><strong>Digital:</strong> App Store, Google Play, Instagram/TikTok/Facebook ads, LinkedIn crew groups, SEO & ASO</p>
              <p><strong>Physical:</strong> Airport crew lounges, airline crew bases, hotel crew desks, flight attendant union newsletters</p>
              <p><strong>Referral:</strong> Crew referral ($10 per FA), sender referral, airline employee discount programs</p>
            </div>
          </div>

          {/* Row 3 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 32, position: 'relative', zIndex: 1 }}>
            <div className="bmc-card">
              <h4>Cost Structure</h4>
              <p><strong>Fixed:</strong> Tech maintenance $150K/yr, cloud hosting $100K/yr, staff $500K/yr, legal/compliance $75K/yr, airline partnerships $50K/yr</p>
              <p><strong>Variable:</strong> Payment fees (2.9% + $0.30), app store fees (15-30%), marketing $200K/yr, support scaling $100K/yr, insurance claims</p>
              <p><strong>Already Incurred:</strong> App development $120K, legal setup $25K, pre-launch marketing $30K</p>
            </div>
            <div className="bmc-card" style={{ background: 'linear-gradient(135deg, #059669, #047857)', color: '#fff' }}>
              <h4 style={{ color: '#fff' }}>Revenue Streams</h4>
              <p><strong>1. Subscriptions:</strong> $1/month per user (senders AND couriers) — global flat rate</p>
              <p><strong>2. Per-Transaction:</strong> Tiered by distance & economy (both sides pay). Developed: $4-$40, Mid: $2-$20, Developing: $1.34-$13.34</p>
              <p><strong>3. Insurance:</strong> Premium add-on via AMEX (10-15% commission)</p>
              <p><strong>4. Promotions:</strong> Featured listings ($1-$5), urgent delivery boosts</p>
              <p><strong>5. Airline Partnerships:</strong> Premium placement in crew comms, white-label versions</p>
            </div>
          </div>

          {/* Financial Projection */}
          <div className="section-header" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 24 }}>3-Year Financial Projection</h2>
          </div>
          <div style={{ overflowX: 'auto', position: 'relative', zIndex: 1, marginBottom: 32 }}>
            <table data-testid="financial-table" style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #7851A9, #5B2D8E)', color: '#fff' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left' }}>Metric</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Year 1</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Year 2</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Year 3</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Active Users (Monthly)', '150,000', '750,000', '3,000,000'],
                  ['Flight Attendants (% of couriers)', '20%', '25%', '30%'],
                  ['Total Annual Deliveries', '400,000', '2,000,000', '8,000,000'],
                  ['Subscription Revenue', '$1,800,000', '$9,000,000', '$36,000,000'],
                  ['Base Charge Revenue', '$3,400,000', '$17,500,000', '$72,000,000'],
                  ['Insurance & Promotions', '$340,000', '$2,100,000', '$10,800,000'],
                  ['Total Revenue', '$5,540,000', '$28,600,000', '$118,800,000'],
                  ['Operating Expenses (50%)', '$2,770,000', '$14,300,000', '$59,400,000'],
                  ['Net Income', '$2,770,000', '$14,300,000', '$59,400,000'],
                  ['Cumulative Net Income', '$2,770,000', '$17,070,000', '$76,470,000'],
                ].map(([label, y1, y2, y3], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: label === 'Net Income' || label === 'Total Revenue' ? 'rgba(120,81,169,0.05)' : 'transparent', fontWeight: label === 'Net Income' || label === 'Total Revenue' || label === 'Cumulative Net Income' ? 700 : 400 }}>
                    <td style={{ padding: '10px 16px' }}>{label}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>{y1}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>{y2}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>{y3}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Competitive Advantages */}
          <div className="section-header" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 24 }}>Competitive Advantages</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 14, position: 'relative', zIndex: 1 }}>
            {[
              { title: 'FA Verification Badge', desc: 'Crew upload airline ID for verified badge — trust multiplier for senders' },
              { title: 'Crew Lounge Handoff', desc: 'Airport crew lounges as secure pick-up/drop-off points' },
              { title: 'Airport-to-Airport', desc: 'Optimized for deliveries that start and end at airports' },
              { title: 'Crew-Only Feed', desc: 'Flight attendants filter deliveries matching their exact routes' },
              { title: 'Airline Integration', desc: 'Partner airlines promote via employee apps, payroll integration for earnings' },
              { title: 'Customs Pre-Clearance', desc: 'Agreements with customs agencies for pre-cleared package categories' },
              { title: 'Schedule Sync', desc: 'Optional calendar sync with airline schedules — auto-suggests matching deliveries' },
              { title: 'Corporate Accounts', desc: 'Volume discounts for businesses — predictable B2B revenue stream' },
              { title: 'Tax Automation', desc: 'Auto-generated 1099 or equivalent tax forms for couriers' },
              { title: 'Emergency Medical', desc: 'Priority delivery of life-saving medications via crew on next flight' },
            ].map((item, i) => (
              <div key={i} className="bmc-feature">
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 40, position: 'relative', zIndex: 1 }}>
            <a
              href="/investor-portal"
              className="btn btn-primary"
              data-testid="bmc-investor-portal-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}
            >
              <TrendingUp size={18} />
              Access Full Investor Portal
            </a>
            <p style={{ fontSize: 12, color: '#6B7280', marginTop: 12 }}>
              Confidential — NDA Protected
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessModelCanvas;
